import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "src/trackEvent";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { openStandardModal } from "src/common/state/slice/modal/modalSlice";
import type { ScenariaPlan, ScenariaSkyboxPromptPreviewScene } from "src/model/model";
import type { IMedia } from "src/model/unityObject";
import { getScenarioHelperContextImages } from "../../state/chatbotSlice";
import DecorStatusRow from "../referenceImage/DecorStatusRow";
import useRetryImageAnalysis from "../referenceImage/useRetryImageAnalysis";
import OptionSection from "./OptionSection";
import useSkyboxPromptPreview from "./useSkyboxPromptPreview";

const trunkImageModalId = "blockadeTrunkImageModal";

interface BlockadeImagesSectionProps {
  plan?: ScenariaPlan;
  conversationId: number;
  enabled: boolean;
  onToggleEnabled: () => void;
  overrides: Record<string, string>;
  onSetOverride: (sceneId: string, text: string) => void;
  onClearOverride: (sceneId: string) => void;
  isReferenceImageBusy: boolean;
  referenceImageRealigningTo: "image" | "ai" | null;
  onPickReferenceImage: () => void;
  onRemoveReferenceImage: (media: IMedia) => void;
}

const CPFAG = "pages.chat.createProjectFromAiGraph";
const CG = "pages.chat.configureGeneration";

const TRUNK_TINT = "bg-purple-50 text-purple-700 border-purple-200";
const VARIATION_TINT = "bg-amber-50 text-amber-700 border-amber-200";

const BlockadeImagesSection = ({
  plan,
  conversationId,
  enabled,
  onToggleEnabled,
  overrides,
  onSetOverride,
  onClearOverride,
  isReferenceImageBusy,
  referenceImageRealigningTo,
  onPickReferenceImage,
  onRemoveReferenceImage,
}: BlockadeImagesSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contextImages = useAppSelector(getScenarioHelperContextImages);

  const [open, setOpen] = useState(enabled);
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  if (prevEnabled !== enabled) {
    setPrevEnabled(enabled);
    setOpen(enabled);
  }

  const [editing, setEditing] = useState<Set<string>>(new Set());
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});

  const toggleEditing = (sceneId: string) => {
    setEditing((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
  };

  const scenes = plan?.sceneOutline ?? [];
  const imageSceneCount = scenes.filter((s) => s.mediaType === "image360").length;

  const planStamp = plan?.lastRefinedAt ?? plan?.generatedAt;

  const [prevPlanStamp, setPrevPlanStamp] = useState(planStamp);
  if (prevPlanStamp !== planStamp) {
    setPrevPlanStamp(planStamp);
    setEditing(new Set());
    setDraftValues({});
  }

  const isRealigning = !!referenceImageRealigningTo;
  const {
    status,
    scenes: previewScenes,
    trunk,
    retry,
    refresh,
  } = useSkyboxPromptPreview(conversationId, planStamp, enabled || open);

  const trunkMedia =
    trunk?.source === "attachedImages"
      ? contextImages.find((m) => trunk.mediaIds?.includes(m.id))
      : undefined;

  const { isRetrying: isRetryingVision, retry: retryImageAnalysis } = useRetryImageAnalysis(
    conversationId,
    refresh,
  );

  const previewById = useMemo(() => {
    const map: Record<string, ScenariaSkyboxPromptPreviewScene> = {};
    previewScenes.forEach((s) => {
      map[s.sceneId] = s;
    });
    return map;
  }, [previewScenes]);

  const handleToggleEnabled = () => {
    trackEvent("blockade-toggle", { enabled: !enabled });
    onToggleEnabled();
  };

  const summary = enabled
    ? t(`${CG}.blockadeSummaryOn`, { count: imageSceneCount })
    : t(`${CG}.blockadeSummaryOff`);

  const commit = (sceneId: string, text: string, serverPrompt: string) => {
    if (text.trim() && text !== serverPrompt) onSetOverride(sceneId, text);
    else onClearOverride(sceneId);
  };

  const reset = (sceneId: string) => {
    onClearOverride(sceneId);
    setDraftValues((prev) => {
      const next = { ...prev };
      delete next[sceneId];
      return next;
    });
    setEditing((prev) => {
      const next = new Set(prev);
      next.delete(sceneId);
      return next;
    });
  };

  const inputClasses =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500";

  const renderPromptBody = (
    scene: ScenariaSkyboxPromptPreviewScene | undefined,
    overrideText: string | undefined,
  ) => {
    if (overrideText !== undefined) {
      return <p className="text-sm leading-snug text-gray-700">{overrideText}</p>;
    }
    if (!scene || !scene.prompt) return null;
    if (scene.source === "b2" && scene.trunk && scene.variation) {
      return (
        <p className="text-sm leading-snug">
          <span className={`rounded border px-1 ${TRUNK_TINT}`}>{scene.trunk}</span>
          <span className="text-gray-500">{", "}</span>
          <span className={`rounded border px-1 ${VARIATION_TINT}`}>{scene.variation}</span>
        </p>
      );
    }
    return (
      <>
        <p className="text-sm leading-snug text-gray-700">{scene.prompt}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{t(`${CG}.promptNotDecomposable`)}</p>
      </>
    );
  };

  return (
    <OptionSection
      id="blockade-images"
      title={t(`${CPFAG}.generateBlockadeImages`)}
      enabled={enabled}
      onToggleEnabled={handleToggleEnabled}
      summary={summary}
      open={open}
      onOpenChange={setOpen}
    >
      <p className="text-xs text-gray-600 mb-2">
        {enabled ? t(`${CG}.blockadeIntro`) : t(`${CG}.blockadeIntroDisabled`)}
      </p>

      <DecorStatusRow
        plan={plan}
        contextImages={contextImages}
        busy={isReferenceImageBusy}
        onPick={onPickReferenceImage}
        onRemove={onRemoveReferenceImage}
        onView={trunkMedia ? () => dispatch(openStandardModal(trunkImageModalId)) : undefined}
        onRetryAnalysis={retryImageAnalysis}
        isRetryingAnalysis={isRetryingVision}
        realigningTo={referenceImageRealigningTo}
        className="mb-2 rounded-md bg-purple-50/40 px-2 py-1.5"
      />

      {status === "ready" && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TRUNK_TINT}`}>
            {t(`${CG}.legendTrunk`)}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${VARIATION_TINT}`}>
            {t(`${CG}.legendVariation`)}
          </span>
        </div>
      )}

      {status === "loading" && (
        <div className="space-y-2" aria-hidden>
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-gray-50 rounded-md p-2.5">
              <div className="animate-pulse bg-gray-100 rounded h-4 w-1/3 mb-2" />
              <div className="animate-pulse bg-gray-100 rounded h-4 w-full" />
            </div>
          ))}
        </div>
      )}

      {status === "error" && (
        <div className="text-sm text-gray-600">
          <p>{t(`${CG}.previewError`)}</p>
          <button
            type="button"
            onClick={retry}
            className="text-purple-500 hover:text-purple-600 font-medium text-xs mt-1"
          >
            {t(`${CG}.previewRetry`)}
          </button>
        </div>
      )}

      {status === "ready" && (
        <div className="space-y-1.5">
          {scenes.map((scene, i) => {
            if (scene.mediaType === "video360") {
              return (
                <div key={scene.sceneId || i} className="bg-gray-50 rounded-md p-2.5 opacity-60">
                  <p className="text-sm font-semibold text-gray-800">
                    <span className="mr-1.5 font-normal text-gray-400">
                      {t("general.sceneNumber", { n: i + 1 })}
                    </span>
                    {scene.title}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {t(`${CG}.noImageForVideo360`)}
                  </p>
                </div>
              );
            }

            const preview = previewById[scene.sceneId];
            const serverPrompt = preview?.prompt ?? "";
            const hasOverride = Object.prototype.hasOwnProperty.call(overrides, scene.sceneId);
            const isEditing = editing.has(scene.sceneId);
            const value = hasOverride ? overrides[scene.sceneId] : serverPrompt;
            const draft = draftValues[scene.sceneId];
            const textareaValue = draft !== undefined ? draft : value;

            return (
              <div key={scene.sceneId || i} className="bg-gray-50 rounded-md p-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-800">
                    <span className="mr-1.5 font-normal text-gray-400">
                      {t("general.sceneNumber", { n: i + 1 })}
                    </span>
                    {scene.title}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasOverride && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded border ${VARIATION_TINT}`}
                      >
                        {t(`${CG}.overriddenBadge`)}
                      </span>
                    )}
                    {enabled && (
                      <button
                        type="button"
                        onClick={() => toggleEditing(scene.sceneId)}
                        disabled={isRealigning}
                        className="text-purple-500 hover:text-purple-600 font-medium text-xs disabled:opacity-50"
                      >
                        {t("general.edit")}
                      </button>
                    )}
                    {enabled && hasOverride && (
                      <button
                        type="button"
                        onClick={() => reset(scene.sceneId)}
                        disabled={isRealigning}
                        className="text-purple-500 hover:text-purple-600 font-medium text-xs disabled:opacity-50"
                      >
                        {t(`${CG}.resetOverride`)}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1.5">
                  {enabled && isEditing ? (
                    <textarea
                      rows={2}
                      maxLength={400}
                      value={textareaValue}
                      disabled={isRealigning}
                      onChange={(e) =>
                        setDraftValues((prev) => ({
                          ...prev,
                          [scene.sceneId]: e.target.value,
                        }))
                      }
                      onBlur={() => commit(scene.sceneId, textareaValue, serverPrompt)}
                      className={`${inputClasses} disabled:opacity-60`}
                    />
                  ) : (
                    renderPromptBody(preview, hasOverride ? value : undefined)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {trunkMedia && (
        <ModalStandard id={trunkImageModalId} modalType="standard" className="w-[36rem]">
          <div className="p-2 space-y-2">
            <p className="text-sm font-semibold text-gray-800">
              {trunkMedia.filename || trunkMedia.name}
            </p>
            <img
              className="object-contain w-full max-h-[60vh] rounded-md"
              src={trunkMedia.s3_url}
              alt={trunkMedia.filename || trunkMedia.name}
            />
            <p className="text-xs text-gray-500">{t(`${CG}.trunkImageModalHint`)}</p>
          </div>
        </ModalStandard>
      )}
    </OptionSection>
  );
};

export default BlockadeImagesSection;
